import { useState, isValidElement, CSSProperties, ReactNode } from 'react';

import { logger } from '@azure/storage-blob';
import CheckIcon from '@mui/icons-material/Check';
import { Box, IconButton, Typography, Tooltip, useTheme } from '@mui/material';

import { IcGPS, IcCopy } from '@/assets/images';
import { ConfirmTypes, useToastStore } from '@/store';
import { colors } from '@/theme/variables';

interface CopyableWrapperProps {
  label: string;
  value: string | number | boolean | null;
  valueStyle?: CSSProperties;
  valueAsTooltip?: boolean;
  showMapsIcon?: boolean;
  getCopyValue?: () => Promise<string>;
  persistentActions?: ReactNode;
}

async function copyToClipboard(textToCopy: string) {
  if (navigator.clipboard && window.isSecureContext) {
    await navigator.clipboard.writeText(textToCopy);
  } else {
    const textArea = document.createElement('textarea');
    textArea.value = textToCopy;

    textArea.style.position = 'absolute';
    textArea.style.left = '-999999px';

    document.body.prepend(textArea);
    textArea.select();

    try {
      document.execCommand('copy');
    } catch (error) {
      logger.error('Error', error);
    } finally {
      textArea.remove();
    }
  }
}

function openInGoogleMaps(address: string) {
  const encodedAddress = encodeURIComponent(address);
  window.open(
    `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`,
    '_blank',
  );
}

export default function CopyableWrapper({
  label,
  value,
  valueStyle,
  showMapsIcon = false,
  getCopyValue,
  persistentActions,
}: CopyableWrapperProps) {
  const theme = useTheme();
  const [hovered, setHovered] = useState(false);
  const [copied, setCopied] = useState(false);
  const { updateToast } = useToastStore();

  const isDark = theme.palette.mode === 'dark';
  const plainValue = isValidElement(value) ? '' : value?.toString() ?? '';
  const hasValue = plainValue !== '';

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const textToCopy = getCopyValue ? await getCopyValue() : plainValue;
    if (typeof textToCopy === 'string') {
      copyToClipboard(textToCopy);
      setCopied(true);
      updateToast({
        message: 'Copied!',
        type: ConfirmTypes.SUCCESS,
        open: true,
      });
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleMapsClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (plainValue) {
      openInGoogleMaps(plainValue);
    }
  };

  const hoverBorder = isDark ? theme.palette.divider : colors.grey[10];
  const hoverBg = isDark ? theme.palette.action.hover : '#F1F3F4';

  return (
    <Box
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        borderRadius: '8px',
        padding: '8px',
        transition: 'all 0.2s ease',
        position: 'relative',
        gap: '4px',
        cursor: 'default',
        border:
          hasValue && hovered
            ? `1px solid ${hoverBorder}`
            : '1px solid transparent',
        backgroundColor: hasValue && hovered ? hoverBg : 'transparent',
        borderColor: hasValue && hovered ? hoverBorder : 'transparent',
      }}
    >
      {label && (
        <Typography
          sx={{
            fontSize: '14px',
            fontWeight: 500,
            color: isDark ? 'text.secondary' : colors.grey[70],
          }}
        >
          {label}
        </Typography>
      )}

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
        }}
      >
        {value !== null && value !== undefined && (
          <Typography
            sx={{
              fontSize: '14px',
              wordBreak: 'break-word',
              lineHeight: '20px',
              flex: 1,
              fontWeight: 600,
              color: 'text.primary',
              ...valueStyle,
            }}
          >
            {isValidElement(value) ? value : plainValue}
          </Typography>
        )}

        {(value || persistentActions) && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              flexShrink: 0,
            }}
          >
            {showMapsIcon && value && (
              <Tooltip title="Open in Google Maps" placement="top">
                <IconButton
                  size="small"
                  onClick={handleMapsClick}
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '6px',
                    border: `1px solid ${
                      isDark ? theme.palette.divider : colors.grey[20]
                    }`,
                    backgroundColor: theme.palette.background.paper,
                    color: isDark ? 'text.secondary' : colors.grey[80],
                    opacity: hovered ? 1 : 0,
                    transition: 'all 0.2s ease',
                    pointerEvents: hovered ? 'auto' : 'none',
                  }}
                >
                  <IcGPS />
                </IconButton>
              </Tooltip>
            )}
            {value && (
              <Tooltip
                title={copied ? 'Copied!' : 'Click to copy'}
                placement="top"
              >
                <IconButton
                  size="small"
                  onClick={handleCopy}
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '6px',
                    border: `1px solid ${
                      isDark ? theme.palette.divider : colors.grey[20]
                    }`,
                    backgroundColor: theme.palette.background.paper,
                    color: copied
                      ? 'success.main'
                      : isDark
                      ? 'text.secondary'
                      : colors.grey[80],
                    opacity: hovered || copied ? 1 : 0,
                    transition: 'opacity 0.2s ease',
                    pointerEvents: hovered || copied ? 'auto' : 'none',
                  }}
                >
                  {copied ? <CheckIcon fontSize="small" /> : <IcCopy />}
                </IconButton>
              </Tooltip>
            )}
            {persistentActions}
          </Box>
        )}
      </Box>
    </Box>
  );
}
